'use client'

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import type { Post } from '@/lib/api'
import type { Locale } from '@/lib/i18n'

interface SimilarArticlesProps {
  articles: Post[]
  locale: Locale
  categorySlug: string
  maxDisplay?: number
  compact?: boolean
}

export default function SimilarArticles({ 
  articles, 
  locale, 
  categorySlug,
  maxDisplay = 4,
  compact = true
}: SimilarArticlesProps) {
  const [showAll, setShowAll] = useState(false)
  const tui = useTranslations('ui')
  
  if (articles.length === 0) return null

  const displayedArticles = showAll ? articles : articles.slice(0, maxDisplay)

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
      {/* Header avec icône et actions */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-gray-900 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H15" />
          </svg>
          {tui('similarArticles')}
        </h3>
        
        {/* Badge du nombre d'articles */}
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
            {articles.length}
          </span>
        </div>
      </div>

      {/* Liste des articles */}
      <div className="space-y-3">
        {displayedArticles.map((article, index) => (
          <Link 
            key={article.id} 
            href={`/${locale}/${article.category}/${article.slug}`}
            className="group block"
          >
            <article className={`flex gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all duration-200 border border-transparent hover:border-gray-200 hover:shadow-sm ${
              compact ? 'items-center' : 'items-start'
            }`}>
              {/* Image de couverture */}
              <div className={`relative flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 ${
                compact ? 'w-14 h-14' : 'w-20 h-16'
              }`}>
                <Image
                  src={article.coverImage}
                  alt={article.title}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {/* Badge vidéo */}
                {article.type === 'video' && (
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                )}
              </div>

              {/* Contenu */}
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-200 ${
                  compact ? 'text-sm line-clamp-2 leading-tight' : 'text-base line-clamp-2'
                }`}>
                  {article.title}
                </h4>
                
                {/* Métadonnées compactes */}
                <div className="flex items-center gap-2 mt-2">
                  <time className="text-xs text-gray-500">
                    {new Date(article.publishedAt).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short'
                    })}
                  </time>
                  
                  {article.readingTime && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span className="text-xs text-gray-500">
                        {article.readingTime} min
                      </span>
                    </>
                  )}
                  

                </div>

                {/* Position dans la liste pour les premiers articles */}
                {index < 3 && (
                  <div className="absolute -left-2 -top-2 w-6 h-6 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
                    {index + 1}
                  </div>
                )}
              </div>
            </article>
          </Link>
        ))}
      </div>

      {/* Actions en bas */}
      <div className="mt-5 pt-4 border-t border-gray-100 space-y-3">
        {/* Bouton Voir plus/moins */}
        {articles.length > maxDisplay && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <svg className={`w-4 h-4 transition-transform ${showAll ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {showAll ? tui('seeLess') : tui('seeMore', { count: articles.length - maxDisplay })}
          </button>
        )}
        
        {/* Lien vers la catégorie */}
        <Link 
          href={`/${locale}/${categorySlug}`}
          className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors duration-200"
        >
          {tui('allArticlesCategory')}
        </Link>
      </div>

      {/* Stats en bas */}
      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
        <span>{tui('updatedRecently')}</span>
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          {tui('recommended')}
        </span>
      </div>
    </div>
  )
}